import { useLazyFetchGraphql } from '@zoopla/fetch-graphql';
import { GraphQLError } from 'graphql';
import {
    UpdateUserProfileDocument,
    UpdateUserProfileMutation
} from '@zoopla/user-queries';
import { trackSegmentAndGAEvent } from '@zoopla/tracking';

const getTrackingLabel = (situationValue: Record<string, string>) =>
    Object.entries(situationValue)
        .map(([key, value]) => (value ? `${key}:${value}` : undefined))
        .filter(Boolean)
        .join(',');

export const UNSAVED_STORAGE_KEY = 'unsavedSituationAnswers';

export const useSaveGuestSituationData = () => {
    const { lazyFetch: updateUserProfile } =
        useLazyFetchGraphql<UpdateUserProfileMutation>(
            UpdateUserProfileDocument
        );

    const hasSavedAnswers = (): boolean => {
        if (typeof window === 'undefined') {
            return false;
        }

        return sessionStorage?.getItem(UNSAVED_STORAGE_KEY) !== null;
    };

    const getSavedAnswers = () => {
        if (typeof window === 'undefined') {
            return;
        }

        return sessionStorage?.getItem(UNSAVED_STORAGE_KEY);
    };

    const removeSavedAnswers = () => {
        if (typeof window === 'undefined') {
            return;
        }

        return sessionStorage?.removeItem(UNSAVED_STORAGE_KEY);
    };

    const saveSituationData = async (): Promise<
        | {
              data?: UpdateUserProfileMutation | undefined;
              error?: GraphQLError[] | null | undefined;
          }
        | undefined
    > => {
        if (typeof window === 'undefined') {
            return;
        }

        const unsavedSituationInStorage =
            sessionStorage?.getItem(UNSAVED_STORAGE_KEY) ?? null;

        if (unsavedSituationInStorage === null) {
            throw new Error('Cannot find any saved answers');
        }

        const unsavedData = JSON.parse(unsavedSituationInStorage);

        if (Object.keys(unsavedData).length === 0) {
            return;
        }

        // add flag to localstorage to avoid showing modal again
        localStorage.setItem('your-situation-modal-lsrp-completed', 'true');

        try {
            const updatedProfile = await updateUserProfile({
                input: {
                    ...unsavedData
                }
            });

            if (updatedProfile.errors || !updatedProfile.data) {
                throw new Error(
                    'Error saving user your situation data from sessionStorage'
                );
            }

            trackSegmentAndGAEvent(
                'My Situation Submission',
                {
                    eventCategory: 'situation',
                    eventAction: 'click',
                    eventLabel: `auth: ${getTrackingLabel(unsavedData)}`
                },
                // as part of CRME-2416 we decided this value is not going to be
                // critical for our tracking purposes and to default it to `authentication`
                'authentication'
            );

            return updatedProfile;
        } catch {
            throw new Error(
                'Error saving user your situation data from sessionStorage'
            );
        }
    };

    return {
        saveSituationData,
        hasSavedAnswers,
        getSavedAnswers,
        removeSavedAnswers
    };
};
